import {useCurrentRepository} from '@github-ui/current-repository'
import {codeNavGeneralSearchPath, codeNavSearchPath} from '@github-ui/paths'
import {SearchIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'
import {useEffect, useEffectEvent, useRef, useState} from 'react'

import styles from './SymbolZeroState.module.css'

export function SymbolZeroState({filterText, isFindInFile}: {filterText?: string; isFindInFile?: boolean}) {
  const [message, setMessage] = useState(isFindInFile ? 'No matches found' : 'No symbols found')
  const repo = useCurrentRepository()

  const firstUpdateRef = useRef(true)

  const handleAriaMessageAnnounceAfterFirstRender = useEffectEvent(() => {
    if (firstUpdateRef.current) {
      firstUpdateRef.current = false
      return
    }

    // This is a hack because aria-live doesn't re-announce updates with the same content
    // But we don't need to do this on the first render or else aria-live will announce twice
    setMessage(prev => `${prev}\u200B`) // 0 width space
  })
  useEffect(() => {
    handleAriaMessageAnnounceAfterFirstRender()
  }, [filterText])

  return (
    <div className={styles.container}>
      {filterText === '' && (
        <>
          <div className={styles.emptyStateCard}>
            <div className={styles.emptyStateCardContent}>
              <h3 className={styles.emptyStateHeading}>Symbol outline not available for this file</h3>
              <div className={styles.emptyStateDescription}>
                To inspect a symbol, try clicking on the symbol directly in the code view.
              </div>
            </div>
          </div>
          <div className={styles.languageSupportNote}>
            {' '}
            Code navigation supports a limited number of languages.{' '}
            <Link
              inline
              href="https://docs.github.com/repositories/working-with-files/using-files/navigating-code-on-github"
            >
              See which languages are supported.
            </Link>
          </div>
        </>
      )}
      {filterText && (
        <>
          <SearchIcon size={24} />
          <h3 className={styles.filterResultHeading} role="alert" aria-relevant="all">
            {message}
          </h3>
        </>
      )}
      {filterText && (
        <span id="filter-zero-state" className={styles.filterZeroState}>
          No lines in this file contain that string.
          <br />
          Search in{' '}
          <Link
            href={codeNavSearchPath({
              owner: repo.ownerLogin,
              repo: repo.name,
              searchTerm: filterText,
            })}
            inline
          >
            {repo.ownerLogin}/{repo.name}
          </Link>{' '}
          or{' '}
          <Link href={codeNavGeneralSearchPath({searchTerm: filterText})} inline>
            all of GitHub
          </Link>
        </span>
      )}
    </div>
  )
}
